import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  HStack,
  Text,
  Box,
  Divider,
  Badge,
  Skeleton,
} from '@chakra-ui/react'
import { Settings, Shield, Mail, Globe } from 'lucide-react'

interface SystemSetting {
  key: string
  value: string
  type: string
  description: string | null
}

interface SystemSettingsCardProps {
  total: number
  keySettings: SystemSetting[]
  byCategory: {
    security: SystemSetting[]
    email: SystemSetting[]
    general: SystemSetting[]
  }
  isLoading?: boolean
}

export function SystemSettingsCard({
  total,
  keySettings,
  byCategory,
  isLoading = false,
}: SystemSettingsCardProps) {
  const formatValue = (value: string, type: string) => {
    if (type === 'boolean') {
      return value === '1' || value === 'true' ? 'Enabled' : 'Disabled'
    }
    return value
  }

  const getSettingIcon = (key: string) => {
    if (key.includes('two_factor') || key.includes('security') || key.includes('login') || key.includes('password')) {
      return <Shield size={16} />
    }
    if (key.includes('mail') || key.includes('email')) {
      return <Mail size={16} />
    }
    return <Settings size={16} />
  }

  if (isLoading) {
    return (
      <Card bg="white" borderRadius="md" boxShadow="sm">
        <CardHeader>
          <Heading size="md">System Configuration</Heading>
        </CardHeader>
        <CardBody>
          <Skeleton height="200px" width="100%" />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card bg="white" borderRadius="md" boxShadow="sm">
      <CardHeader pb={2}>
        <HStack justify="space-between" align="center">
          <Heading size="md">System Configuration</Heading>
          <Badge colorScheme="blue" fontSize="xs">
            {total} settings
          </Badge>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <VStack spacing={4} align="stretch">
          {/* Security Settings */}
          {byCategory.security && byCategory.security.length > 0 && (
            <Box>
              <HStack spacing={2} mb={2}>
                <Shield size={16} color="#E53E3E" />
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                  Security Settings
                </Text>
              </HStack>
              <VStack spacing={2} align="stretch" pl={6}>
                {byCategory.security.map((setting) => (
                  <HStack key={setting.key} justify="space-between" fontSize="xs">
                    <Text color="gray.600">{setting.description || setting.key.replace(/_/g, ' ')}</Text>
                    <Badge
                      colorScheme={setting.value === '1' || setting.value === 'true' ? 'green' : 'gray'}
                      fontSize="2xs"
                    >
                      {formatValue(setting.value, setting.type)}
                    </Badge>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {/* Email Settings */}
          {byCategory.email && byCategory.email.length > 0 && (
            <Box>
              <Divider />
              <HStack spacing={2} mb={2} mt={2}>
                <Mail size={16} color="#3182CE" />
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                  Email Settings
                </Text>
              </HStack>
              <VStack spacing={2} align="stretch" pl={6}>
                {byCategory.email.map((setting) => (
                  <HStack key={setting.key} justify="space-between" fontSize="xs">
                    <Text color="gray.600">{setting.description || setting.key.replace(/_/g, ' ')}</Text>
                    <Text color="gray.700" fontWeight="medium">
                      {formatValue(setting.value, setting.type)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {/* General Settings */}
          {byCategory.general && byCategory.general.length > 0 && (
            <Box>
              <Divider />
              <HStack spacing={2} mb={2} mt={2}>
                <Globe size={16} color="#38A169" />
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                  General Settings
                </Text>
              </HStack>
              <VStack spacing={2} align="stretch" pl={6}>
                {byCategory.general.map((setting) => (
                  <HStack key={setting.key} justify="space-between" fontSize="xs">
                    <Text color="gray.600">{setting.description || setting.key.replace(/_/g, ' ')}</Text>
                    <Text color="gray.700" fontWeight="medium">
                      {formatValue(setting.value, setting.type)}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {(!byCategory.security || byCategory.security.length === 0) &&
            (!byCategory.email || byCategory.email.length === 0) &&
            (!byCategory.general || byCategory.general.length === 0) && (
              <Box textAlign="center" py={4}>
                <Text color="gray.500" fontSize="sm">
                  No system settings available
                </Text>
              </Box>
            )}
        </VStack>
      </CardBody>
    </Card>
  )
}
